import { localizeWaitingRoomOpensMessage } from '@/lib/telehealth/joinWindow';
import { Button, Center, Select, Stack, Text } from '@mantine/core';
import { useMedplum } from '@medplum/react-hooks';
import { useCallback, useEffect, useRef, useState } from 'react';
import {
  clientWaitStep,
  executeChimeTelehealth,
  releaseChimeSessionOnUnload,
  type ChimeJoinResult,
} from '@/lib/telehealth/chime/chimeTelehealthService';
import { chimeAttendeeDisplayName } from '@/lib/telehealth/chime/chimeParticipantVisuals';
import { midCallRecordingNoticeOpen } from '@/lib/telehealth/chime/chimeRecording';
import { microphoneJoinState } from '@/lib/telehealth/chime/chimeDevices';
import { droppedCallNotice } from '@/lib/telehealth/chime/chimeMeetingStatus';
import { useChimeSessionStatus } from '@/lib/telehealth/chime/useChimeSessionStatus';
import { useChimeRecordingShown } from '@/lib/telehealth/chime/useChimeRecordingShown';
import { useChimeMeetingSession } from '@/lib/telehealth/chime/useChimeMeetingSession';
import { useDevicePreview } from '@/lib/telehealth/chime/useDevicePreview';
import { ChimeJoinPreview } from '@/pages/session/components/ChimeJoinPreview';
import { ChimeMediaStage } from '@/pages/session/components/ChimeMediaStage';
import { CallEndedScreen } from '@/pages/session/components/CallEndedScreen';
import { RECORDING_NOTICE_MID_CALL_LABEL, RecordingNoticeModal } from '@/pages/session/components/RecordingNoticeModal';
import { ProviderAbsentNotice, ProviderLateNotice } from '@/pages/session/components/ProviderAbsentNotice';

const WAIT_POLL_MS = 3_000;

export function ClientChimeSession({
  appointmentId,
  clientName,
  onLeave,
}: {
  appointmentId: string;
  clientName: string;
  onLeave: () => void;
}): JSX.Element {
  const medplum = useMedplum();
  const preview = useDevicePreview();
  const session = useChimeMeetingSession();
  const [phase, setPhase] = useState<
    'preview' | 'waiting' | 'recording-notice' | 'connecting' | 'in-call' | 'ended'
  >('preview');
  // The client accepted the recording notice in this page session (before joining, or mid-call).
  const [acknowledged, setAcknowledged] = useState(false);
  const [providerLate, setProviderLate] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  // The call dropped: the preview offers Join again.
  const [dropped, setDropped] = useState(false);
  const startedRef = useRef(false);
  const aliveRef = useRef(true);
  // A client-wait poll (and the connect it may start) is running; interval ticks skip until it ends.
  const pollInFlightRef = useRef(false);
  // Set once we ask to wait (the server may record us before the response); holds our
  // attendee once admitted. Cleared after an explicit leave.
  const claimRef = useRef<{ attendeeId?: string } | null>(null);
  const startRef = useRef(session.start);
  startRef.current = session.start;
  const stopPreviewRef = useRef(preview.stopPreview);
  stopPreviewRef.current = preview.stopPreview;
  const restartPreviewRef = useRef(preview.restartPreview);
  restartPreviewRef.current = preview.restartPreview;
  const audioDeviceId = preview.audioDeviceId;
  const videoDeviceId = preview.videoDeviceId;
  const startMuted = !preview.micOn;
  const cameraOff = !preview.cameraOn;
  // Recording comes from Chime's roster (Decision 39); the wait response's `recordingActive` covers the connect.
  const { recording: recordingActive, expect: expectRecording } = useChimeRecordingShown(session.recorderPresent);

  const connect = useCallback(
    async (result: ChimeJoinResult) => {
      if (!result.meeting || !result.attendee) return;
      setPhase('connecting');
      expectRecording(result.recordingActive ? true : undefined);
      await stopPreviewRef.current();
      if (!aliveRef.current) return;
      const joined = await startRef.current(result.meeting, result.attendee, {
        audioDeviceId,
        videoDeviceId,
        requireAudio: true,
        startMuted,
        cameraOff,
      });
      if (!aliveRef.current) return;
      if (joined) {
        setDropped(false);
        setPhase('in-call');
        return;
      }
      // Release the admitted attendee so the provider does not see a participant who never arrived.
      claimRef.current = null;
      startedRef.current = false;
      await executeChimeTelehealth(medplum, {
        action: 'leave',
        appointmentId,
        attendeeId: result.attendee.AttendeeId,
      }).catch(() => undefined);
      if (!aliveRef.current) return;
      setError('Could not connect to the session. Please try again.');
      setPhase('preview');
      restartPreviewRef.current();
    },
    [appointmentId, audioDeviceId, cameraOff, expectRecording, medplum, startMuted, videoDeviceId],
  );

  /**
   * When the session is recording the bot withholds credentials until the client has seen
   * the notice; `recordingAcknowledged` is sent only from the notice's join button.
   */
  const pollWait = useCallback(
    async (recordingAcknowledged = false) => {
      if (pollInFlightRef.current) return;
      pollInFlightRef.current = true;
      claimRef.current ??= {};
      try {
        const result = (await executeChimeTelehealth(medplum, {
          action: 'client-wait',
          appointmentId,
          ...(recordingAcknowledged ? { recordingAcknowledged: true } : {}),
        })) as ChimeJoinResult;
        if (!aliveRef.current) return;
        if (claimRef.current && result.attendee?.AttendeeId) {
          claimRef.current.attendeeId = result.attendee.AttendeeId;
        }
        setProviderLate(Boolean(result.providerLate));
        const step = clientWaitStep(result, startedRef.current);
        if (step === 'acknowledge') {
          setPhase('recording-notice');
        } else if (step === 'connect') {
          startedRef.current = true;
          await connect(result);
        } else if (step === 'waiting') {
          setPhase('waiting');
        } else if (step === 'ended') {
          await stopPreviewRef.current();
          setPhase('ended');
        }
      } catch (e) {
        if (!aliveRef.current) return;
        setError(e instanceof Error ? localizeWaitingRoomOpensMessage(e.message) : 'Could not join the waiting room');
        if (recordingAcknowledged) {
          // Back to preview so "Join waiting room" can retry (and show the notice again).
          startedRef.current = false;
          setPhase('preview');
          restartPreviewRef.current();
        }
      } finally {
        pollInFlightRef.current = false;
      }
    },
    [appointmentId, connect, medplum],
  );

  const acknowledgeRecording = useCallback(async () => {
    setError(null);
    setAcknowledged(true);
    await pollWait(true);
  }, [pollWait]);

  const enterWaiting = useCallback(async () => {
    setBusy(true);
    setError(null);
    try {
      await pollWait();
    } finally {
      setBusy(false);
    }
  }, [pollWait]);

  useEffect(() => {
    if (phase !== 'waiting') return;
    const id = setInterval(() => {
      void pollWait();
    }, WAIT_POLL_MS);
    return () => clearInterval(id);
  }, [phase, pollWait]);

  const inCall = phase === 'in-call' && session.status === 'in-meeting';
  // The provider can leave; the provider-left message follows the poll. The badge follows the roster.
  const { status: recordingStatus } = useChimeSessionStatus(medplum, appointmentId, inCall);
  // Recording started after admission (e.g. the provider's Resume): the notice, client-side; Leave declines.
  const midCallNoticeOpen = midCallRecordingNoticeOpen({
    inCall,
    recording: session.recorderPresent,
    acknowledged,
  });

  // Closing the tab or navigating away in-app must release the waiting-room claim and the
  // Chime attendee, otherwise the provider is left with a row that cannot be admitted or a
  // participant that never left.
  const release = useCallback(() => {
    const claim = claimRef.current;
    claimRef.current = null;
    if (!claim) return;
    releaseChimeSessionOnUnload(medplum, {
      appointmentId,
      ...(claim.attendeeId ? { attendeeId: claim.attendeeId } : {}),
    });
  }, [appointmentId, medplum]);

  useEffect(() => {
    aliveRef.current = true;
    window.addEventListener('pagehide', release);
    return () => {
      aliveRef.current = false;
      window.removeEventListener('pagehide', release);
      release();
    };
  }, [release]);

  // The connection dropped mid-call: release the attendee (as the provider's call page does) and go back
  // to the preview to join again. Not when this attendee is live on another device: leaving would drop it there.
  useEffect(() => {
    if (phase !== 'in-call' || session.status !== 'error') return;
    const notice = droppedCallNotice(session.errorCode);
    const claim = claimRef.current;
    const attendeeId = session.attendeeId ?? claim?.attendeeId;
    claimRef.current = null;
    startedRef.current = false;
    if (!notice.elsewhere && (attendeeId || claim)) {
      void executeChimeTelehealth(medplum, {
        action: 'leave',
        appointmentId,
        ...(attendeeId ? { attendeeId } : {}),
      }).catch(() => undefined);
    }
    setError(notice.message);
    setDropped(true);
    setPhase('preview');
    restartPreviewRef.current();
  }, [appointmentId, medplum, phase, session.attendeeId, session.errorCode, session.status]);

  /** Also declines the recording notice: before joining, that releases the admitted attendee. */
  const handleLeave = useCallback(async () => {
    const claim = claimRef.current;
    const attendeeId = session.attendeeId ?? claim?.attendeeId;
    claimRef.current = null;
    // Mic and camera stop now, not after the bot round trip.
    session.stop();
    // Declining from the waiting or notice screen: the local camera/microphone preview is still running.
    await stopPreviewRef.current();
    if (attendeeId || claim) {
      try {
        await executeChimeTelehealth(medplum, {
          action: 'leave',
          appointmentId,
          ...(attendeeId ? { attendeeId } : {}),
        });
      } catch {
        /* already left */
      }
    }
    onLeave();
  }, [appointmentId, medplum, onLeave, session]);

  // Never join with the SDK's silent microphone: wait for the prompt, and require a device.
  const microphone = microphoneJoinState(preview.access, preview.audioDeviceId);

  if (session.endedByHost || phase === 'ended') {
    return <CallEndedScreen onDone={onLeave} />;
  }

  if (phase === 'in-call' || session.status === 'in-meeting') {
    return (
      <Stack h="100dvh" gap={0} style={{ overflow: 'hidden' }}>
        <RecordingNoticeModal
          opened={midCallNoticeOpen}
          acknowledgeLabel={RECORDING_NOTICE_MID_CALL_LABEL}
          onAcknowledge={() => setAcknowledged(true)}
          onDecline={() => void handleLeave()}
        />
        {recordingStatus?.providerPresent === false ? <ProviderAbsentNotice /> : null}
        {error || session.error ? (
          <Text c="red" px="md" pt="sm">
            {error ?? session.error}
          </Text>
        ) : null}
        <ChimeMediaStage
          tiles={session.tiles}
          bindTile={session.bindTile}
          bindAudio={session.bindAudio}
          muted={session.muted}
          cameraOn={session.cameraOn}
          onToggleMute={session.toggleMute}
          onToggleCamera={() => void session.toggleCamera()}
          onEnd={() => void handleLeave()}
          activeSpeakerId={session.activeSpeakerId}
          attendeeExternalUserIds={session.attendeeExternalUserIds}
          localDisplayName={clientName}
          displayNameForExternalUserId={chimeAttendeeDisplayName}
          recording={recordingActive}
          screenShare={{
            sharing: session.sharing,
            onStart: () => void session.startScreenShare(),
            onStop: session.stopScreenShare,
          }}
          localAttendeeId={session.attendeeId}
          endLabel="Leave call"
        />
      </Stack>
    );
  }

  return (
    <>
      <RecordingNoticeModal
        opened={phase === 'recording-notice'}
        onAcknowledge={() => void acknowledgeRecording()}
        onDecline={() => void handleLeave()}
      />
      <Center h="60vh">
        <Stack align="center" gap="md" maw={600}>
          <Text size="lg" fw={500}>
            {phase === 'waiting'
              ? 'Waiting for your provider'
              : phase === 'connecting'
                ? 'Connecting…'
                : 'Preview your camera'}
          </Text>
          {error || preview.error ? <Text c="red">{error ?? preview.error}</Text> : null}
          <ChimeJoinPreview preview={preview} />
          {preview.access === 'blocked' ? (
            <Button variant="light" onClick={preview.requestAccess}>
              Allow camera &amp; microphone
            </Button>
          ) : null}
          <Select
            label="Microphone"
            data={preview.audioDevices.map((d) => ({ value: d.deviceId, label: d.label }))}
            value={preview.audioDeviceId}
            onChange={(v) => v && preview.setAudioDeviceId(v)}
            disabled={preview.audioDevices.length === 0}
            placeholder={preview.audioDevices.length === 0 ? 'No microphone detected' : undefined}
            w="100%"
          />
          <Select
            label="Camera"
            data={preview.videoDevices.map((d) => ({ value: d.deviceId, label: d.label }))}
            value={preview.videoDeviceId}
            onChange={(v) => v && preview.setVideoDeviceId(v)}
            disabled={preview.videoDevices.length === 0}
            placeholder={preview.videoDevices.length === 0 ? 'No camera detected' : undefined}
            w="100%"
          />
          {phase === 'waiting' ? (
            <>
              {providerLate ? <ProviderLateNotice /> : null}
              <Text size="sm" c="dimmed" ta="center">
                You&apos;ll join automatically when your provider admits you.
              </Text>
            </>
          ) : phase === 'preview' ? (
            <>
              {microphone.notice ? (
                <Text c="red" size="sm" ta="center">
                  {microphone.notice}
                </Text>
              ) : null}
              <Button
                size="lg"
                loading={busy}
                disabled={microphone.disabled}
                onClick={() => void enterWaiting()}
              >
                {dropped ? 'Join again' : 'Join waiting room'}
              </Button>
            </>
          ) : null}
        </Stack>
      </Center>
    </>
  );
}
